import EventsCenter from '@Managers/Events/EventsCenter';
import { Logger, LoggerLevel, LoggerSource } from '@Managers/Logger';

// TYPES
import Reel from './Reel';

// CONFIG
import { GameComponent } from '@Core/GameComponent/GameComponent';
import { GameScene } from '@Core/GameScene/GameScene';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { IMultiSymbolFeature } from '@GFTypes/index';
import { ELayeringOrder } from '@GFTypes/LayeringOrder';
import { EActionName } from '@Managers/Events/EActionName';
import { EGameEvents } from '@Managers/Events/GameEvents';
import { EGameStates } from '@Managers/StateManager/GameStates';
import { StateConditions } from '@Managers/StateManager/StateConditions';
import { Container, Point } from 'pixi.js';
import { SymbolSpinePool } from '../SpinePool/SymbolSpinePool';

export class Reels extends GameComponent {
    public scene: GameScene;
    public reelsContainer: Container;
    public winningSymbolsLayer: Container;
    public winningSymbolsNotOnLineLayer: Container;
    public losingSymbolsLayer: Container;
    public stickySymbolsLayer: Container;
    public stencilReferenceContainer: Container;

    public reelElements: Reel[];

    public symbolSpinePool: SymbolSpinePool;
    public symbolMidpoints: { x: number; y: number }[][];
    public layersCreated: boolean = false;
    constructor(
        scene: GameScene,
        private readonly multiSymbols?: IMultiSymbolFeature
    ) {
        super(scene);
        this.scene = scene;

        Logger.log(LoggerLevel.Constructor, { class: Reels.name, source: LoggerSource.Game, message: 'ReelScene constructor' });
    }

    public onInit(): void {
        // TODO: Dynamically assign all symbols spine keys
        this.symbolSpinePool = new SymbolSpinePool(this.scene, [...this.gameStore.get(EGameStoreKeys.SymbolConfiguration)], this.multiSymbols?.config.asset);

        EventsCenter.on(EGameEvents.QUICK_STOP, this.onQuickStop, this);
        EventsCenter.on(EGameStates.SPIN_STATE, this.startSpin, this);
        EventsCenter.on(EGameEvents.ACTION_RESPONSE_PROCESSED, this.onSpinResultReceived, this);
        EventsCenter.on(EGameEvents.REEL_SPIN_END_$, (data: { eventIds: number[] }) => this.onReelEnded(data.eventIds[0]));
        EventsCenter.on(EGameEvents.RESIZE_REELS, this.onReelsResize, this);
        EventsCenter.on(EGameEvents.UPDATE_VISIBLE_SYMBOLS, (visibleSymbols: number[][]) => this.updateVisibleSymbols(visibleSymbols));
    }

    public onCreate(): void {
        const { reelsSpineScale } = this.gameStore.get(EGameStoreKeys.ReelParameters);
        const symbolsOnReels = this.gameStore.get(EGameStoreKeys.AuthenticateStartSymbols);
        const middlePoint = new Point(this.scene.screen.width / 2, this.scene.screen.height / 2);
        this.reelsContainer = new Container({ position: middlePoint, parent: this.scene.stage, scale: reelsSpineScale, zIndex: ELayeringOrder.REELS });

        this.losingSymbolsLayer = new Container({
            position: new Point(middlePoint.x, middlePoint.y),
            parent: this.scene.stage,
            scale: reelsSpineScale,
            zIndex: ELayeringOrder.LOSING_SYMBOLS_LAYER
        });
        this.winningSymbolsLayer = new Container({
            position: new Point(middlePoint.x, middlePoint.y),
            parent: this.scene.stage,
            scale: reelsSpineScale,
            zIndex: ELayeringOrder.WINNING_SYMBOLS_LAYER
        });
        this.winningSymbolsNotOnLineLayer = new Container({
            position: new Point(middlePoint.x, middlePoint.y),
            parent: this.scene.stage,
            scale: reelsSpineScale,
            zIndex: ELayeringOrder.WINNING_SYMBOLS_NOT_ON_LINE_LAYER
        });
        this.stickySymbolsLayer = new Container({
            position: new Point(middlePoint.x, middlePoint.y),
            parent: this.scene.stage,
            scale: reelsSpineScale,
            zIndex: ELayeringOrder.STICKY_SYMBOLS_LAYER
        });

        this.reelElements = [];
        this.symbolMidpoints = [];

        this.createReels(symbolsOnReels);
        this.layersCreated = true;
    }

    public createReels(symbolsToCreate: number[][]): void {
        const { width, height, reelCenters } = this.gameStore.get(EGameStoreKeys.ReelParameters);

        if (!symbolsToCreate) return console.error('Starting symbols are not available');

        for (const [index, value] of symbolsToCreate.entries()) {
            const originXOffset = width / 2;
            const originYOffset = height / 2;

            const x = (reelCenters[index]?.x ?? 0) - originXOffset;
            const y = (reelCenters[index]?.y ?? 0) - originYOffset;

            const reel = new Reel(this.scene, x, y, value, index, this.multiSymbols);
            this.reelsContainer.addChild(reel);
            reel.init();
            this.reelElements.push(reel);
        }
    }

    private startSpin(): void {
        for (const reel of this.reelElements) {
            reel.saveHistorySymbols();
        }
        EventsCenter.emit(EGameEvents.REEL_ANIMATION_START);
        this.gameStore.set(EGameStoreKeys.VisibleSymbols, []);
        this.gameStore.set(EGameStoreKeys.IsQuickStopInitiated, false);

        for (const reel of this.reelElements) {
            reel.animation.animate();
        }
    }

    public update(delta: number): void {
        if (!this.reelElements) return;
        for (const reel of this.reelElements) {
            if (reel.animation?.onUpdateFunction) {
                reel.animation?.onUpdateFunction(delta);
            }
        }
    }

    private onQuickStop(): void {
        for (const reel of this.reelElements) {
            reel.animation?.quickStop();
        }
    }

    private onSpinResultReceived(action: { actionName: EActionName }): void {
        if (action.actionName !== EActionName.SPIN && action.actionName !== EActionName.BONUS_BUY_SPIN) return;
        const spinResult = this.gameStore.get(EGameStoreKeys.SpinOutcome);

        for (let i = 0; i < this.reelElements.length; i++) {
            this.reelElements[i].animation.passSpinResponse(spinResult.steps[0].finalSymbols[i]);
        }
    }

    private onReelEnded(data: number): void {
        if (data === this.reelElements.length) {
            EventsCenter.emit(EGameEvents.REEL_ANIMATION_END);
            StateConditions.reelsSpinningCondition.resolve();
        }
    }

    private onReelsResize(): void {
        const { reelsSpineScale } = this.gameStore.get(EGameStoreKeys.ReelParameters);
        const middlePoint = new Point(this.scene.screen.width / 2, this.scene.screen.height / 2);
        if (!this.layersCreated) return;
        this.reelsContainer.scale = reelsSpineScale;
        this.reelsContainer.position.copyFrom(middlePoint);
        this.losingSymbolsLayer.scale = reelsSpineScale;
        this.losingSymbolsLayer.position.copyFrom(middlePoint);
        this.winningSymbolsLayer.scale = reelsSpineScale;
        this.winningSymbolsLayer.position.copyFrom(middlePoint);
        this.winningSymbolsNotOnLineLayer.scale = reelsSpineScale;
        this.winningSymbolsNotOnLineLayer.position.copyFrom(middlePoint);
        this.stickySymbolsLayer.scale = reelsSpineScale;
        this.stickySymbolsLayer.position.copyFrom(middlePoint);
    }

    public updateVisibleSymbols(visibleSymbols: number[][]): void {
        EventsCenter.emit(EGameEvents.TAKE);
        if (visibleSymbols) {
            for (let i = 0; i < this.reelElements.length; i++) {
                for (const [index, body] of this.reelElements[i].animation.simulation.sortedBodies().entries()) {
                    const symbol = this.reelElements[i].animation.getSymbolBySimulationBodyId(body.id);
                    const symbolId = visibleSymbols[i][index];
                    if (symbol.isMultiSymbol) {
                        symbol.changeSymbol(
                            visibleSymbols[i][index],
                            this.multiSymbols!.config.asset,
                            i,
                            visibleSymbols[i][index],
                            this.scene.reels.symbolSpinePool
                        );
                        continue;
                    }
                    const symbolData = this.gameStore.get(EGameStoreKeys.SymbolConfiguration).find((symbol) => symbol.id === symbolId);
                    if (symbolData) {
                        symbol.changeSymbol(visibleSymbols[i][index], symbolData.asset, i, visibleSymbols[i][index], this.scene.reels.symbolSpinePool);
                    } else {
                        throw Error('SymbolData does not exist for provided id: ' + visibleSymbols[i][index]);
                    }
                }
            }
        }
    }

    public synchronizeSymbolWithBodies(): void {
        for (const element of this.reelElements) {
            if (!element.animation.simulation) return;
            for (const [index, body] of element.animation.simulation.sortedBodies().entries()) {
                const symbol = element.animation.getSymbolBySimulationBodyId(body.id);
                symbol.position.set(symbol.position.y, body.positionY * symbol.height);
            }
        }
    }

    public destroyReels(): void {
        this.gameStore.set(EGameStoreKeys.VisibleSymbols, []);

        this.reelElements.forEach((reel) => {
            reel.destroy({ children: true });
        });

        this.reelElements = [];
    }
}
